import * as genericService from "./genericFormServices";
import type { LegalAdviceFormTwoData } from "../types/LegalAdvices/LegalAdviceFormTwo.type";
import { type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";

const ENDPOINT = "legal-advice-form-two";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormTwoData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Two
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormTwoData => {
  if (!item) return {} as LegalAdviceFormTwoData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormTwo || item.legalAdviceForm || item.formData || {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_TWO',
    status: item.status || form.status || 'DRAFT',

    // Geo Levels
    originGeoLevel: item.originGeoLevel || '',
    targetGeoLevels: Array.isArray(item.targetGeoLevels)
      ? item.targetGeoLevels
      : [],

    // Form Specific Text Fields (Updated for Form Two)
    office: form.office || '',
    caseYear: form.caseYear || '',
    caseNo: form.caseNo || '',
    date: form.date || '',
    state: form.state || '',
    district: form.district || '',
    township: form.township || '',
    officerState: form.officerState || '',
    officerDistrict: form.officerDistrict || '',
    officerTownship: form.officerTownship || '',
    complaintNo: form.complaintNo || '',
    refNo: form.refNo || '',
    lawSection: form.lawSection || '',
    accusedName: form.accusedName || '',
    courtName: form.courtName || '',
    miscCaseNo: form.miscCaseNo || '',
    fatherName: form.fatherName || '',
    lawOffice: form.lawOffice || '',

    // Signature Details
    signatureImage: form.signatureImage || '',
    signName: form.signName || '',
    signerName: form.signerName || form.singerName || '',
    singerName: form.singerName || form.signerName || '',

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    submittedAt: item.submittedAt || form.submittedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Two
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormTwoData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const matchesDate =
    !selectedDate || Boolean(app.date && app.date.startsWith(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.refNo && app.refNo.toLowerCase().includes(term)) ||
    Boolean(app.accusedName && app.accusedName.toLowerCase().includes(term)) ||
    Boolean(app.lawSection && app.lawSection.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions (Office User - Form Endpoint)
// ---------------------------------------------------------------------------

// Fetch All / Filtered Applications
export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormTwoData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

// Fetch Single Application by ID
export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormTwoData> =>
  genericService.fetchApplicationById<LegalAdviceFormTwoData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

// Save Draft (Create POST or Update PUT)
export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormTwoData>
) => genericService.saveDraft<LegalAdviceFormTwoData>(ENDPOINT, id, data);

// Submit Form (Includes Signatures inside DTO)
export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormTwoData
) => genericService.submitForm<LegalAdviceFormTwoData>(ENDPOINT, id, data);

// Delete Form (Soft Delete)
export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions (Global DocumentController)
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

// Approve Form (PATCH /documents/:id/approve)
export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (
  file: File
) => genericService.uploadFile(file);